import {
  SiSnapchat,
  SiMeta,
  SiTiktok,
  SiPinterest,
  SiYoutube,
  SiGoogle,
  SiFacebook,
  SiInstagram,
  SiX,
  SiSpotify,
  SiApple,
  SiNetflix,
  SiReddit,
  SiTwitch,
  SiWhatsapp,
  SiMessenger,
  SiGoogleads,
} from 'react-icons/si';
import { FaAmazon, FaLinkedin, FaMicrosoft } from 'react-icons/fa6';
import type { IconType } from 'react-icons';

// Platform configuration with icon and brand color
const PLATFORM_CONFIG: Record<string, { icon: IconType; color: string; label: string }> = {
  // Meta platforms
  meta: { icon: SiMeta, color: '#0082FB', label: 'Meta' },
  facebook: { icon: SiFacebook, color: '#1877F2', label: 'Facebook' },
  instagram: { icon: SiInstagram, color: '#E4405F', label: 'Instagram' },
  whatsapp: { icon: SiWhatsapp, color: '#25D366', label: 'WhatsApp' },
  messenger: { icon: SiMessenger, color: '#00B2FF', label: 'Messenger' },

  // Google platforms
  google: { icon: SiGoogle, color: '#4285F4', label: 'Google' },
  youtube: { icon: SiYoutube, color: '#FF0000', label: 'YouTube' },
  'google ads': { icon: SiGoogleads, color: '#4285F4', label: 'Google Ads' },
  'google display': { icon: SiGoogle, color: '#4285F4', label: 'Google Display' },
  dv360: { icon: SiGoogle, color: '#4285F4', label: 'DV360' },

  // Microsoft platforms
  microsoft: { icon: FaMicrosoft, color: '#00A4EF', label: 'Microsoft' },
  'microsoft ads': { icon: FaMicrosoft, color: '#00A4EF', label: 'Microsoft Ads' },
  bing: { icon: FaMicrosoft, color: '#00A4EF', label: 'Bing' },

  // Social platforms
  snapchat: { icon: SiSnapchat, color: '#FFFC00', label: 'Snapchat' },
  snap: { icon: SiSnapchat, color: '#FFFC00', label: 'Snapchat' },
  tiktok: { icon: SiTiktok, color: '#000000', label: 'TikTok' },
  linkedin: { icon: FaLinkedin, color: '#0A66C2', label: 'LinkedIn' },
  pinterest: { icon: SiPinterest, color: '#E60023', label: 'Pinterest' },
  twitter: { icon: SiX, color: '#000000', label: 'X' },
  x: { icon: SiX, color: '#000000', label: 'X' },
  reddit: { icon: SiReddit, color: '#FF4500', label: 'Reddit' },
  twitch: { icon: SiTwitch, color: '#9146FF', label: 'Twitch' },

  // Streaming/Other
  spotify: { icon: SiSpotify, color: '#1DB954', label: 'Spotify' },
  amazon: { icon: FaAmazon, color: '#FF9900', label: 'Amazon' },
  apple: { icon: SiApple, color: '#000000', label: 'Apple' },
  netflix: { icon: SiNetflix, color: '#E50914', label: 'Netflix' },
};

// Aliases that should resolve to a canonical platform key
const PLATFORM_ALIASES: Record<string, string> = {
  'tik tok (douyin)': 'tiktok',
  'tik tok': 'tiktok',
  douyin: 'tiktok',
};

/** Normalize a raw platform string to its canonical PLATFORM_CONFIG key */
export const normalizePlatform = (platform: string): string => {
  const lower = platform?.toLowerCase().trim();
  return PLATFORM_ALIASES[lower] ?? lower;
};

/** Normalize a raw platform string to its canonical display label (e.g. "Tik Tok (Douyin)" → "TikTok") */
export const normalizePlatformLabel = (platform: string): string => {
  const key = normalizePlatform(platform);
  return PLATFORM_CONFIG[key]?.label ?? platform;
};

interface PlatformIconProps {
  platform: string;
  className?: string;
  showLabel?: boolean;
  compact?: boolean;
}

export const PlatformIcon = ({
  platform,
  className = 'w-4 h-4',
  showLabel = true,
  compact = false,
}: PlatformIconProps) => {
  const normalizedPlatform = normalizePlatform(platform);
  const config = PLATFORM_CONFIG[normalizedPlatform];

  const badgeClass = compact
    ? 'px-2 py-0.5 text-xs bg-slate-100 text-slate-700 rounded-md'
    : 'px-2.5 py-1 text-sm bg-slate-100 text-slate-700 rounded-lg';

  if (!config) {
    return (
      <span className={badgeClass}>{platform}</span>
    );
  }

  const Icon = config.icon;

  return (
    <span className={`${badgeClass} inline-flex items-center gap-1.5 whitespace-nowrap`}>
      <Icon className={className} style={{ color: config.color }} />
      {showLabel && <span>{config.label}</span>}
    </span>
  );
};
